import {
  LayoutDashboard, FileSpreadsheet, UserCircle, Landmark, BookOpen, Layers, FileDiff,
  SlidersHorizontal, ShoppingCart, PackageSearch, Truck, Users2, FolderKanban,
  Factory, Warehouse, Building, Wrench, UsersRound, ShieldCheck, BarChart3,
  Scale, Settings2, LineChart, GitFork, DraftingCompass, Boxes, HardHat,
  ClipboardList, BellRing, AlertTriangle, Users, CalendarDays, History, Settings,
  Compass, LayoutGrid, Calculator, Receipt, Clock, FileText, Gauge,
  Percent, Gavel, ScrollText, KeyRound, HeartPulse, TrendingUp,
  Tag, PackageCheck, Hammer, CreditCard, FileMinus, Activity, Package,
  PackageOpen, Undo2, ArrowRightLeft, Building2, Hash, GitBranch, FileSearch,
  PenSquare, Download, Ruler, TrendingDown, ChevronRight as NavChevron, DollarSign,
  FileSignature, Inbox, CheckSquare, PiggyBank,
} from "lucide-react";

// Whether the organisation has more than one company. Set by CompanyContext. One licence is one company, so with a
// single company the company screens are hidden (an organisation that already has several keeps them).
const navFlags = { multiCompany: true };
export function setNavFlags(flags) {
  Object.assign(navFlags, flags);
}

export function slugify(text) {
  return text
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Fixed 11 pages that already exist per-project (Part 1/2 of the DPR platform).
// Only rendered when a :projectId is present in the current route.
export const PROJECT_DPR_PAGES = [
  { suffix: "", label: "Dashboard", icon: LineChart, testId: "sidebar-link-dashboard", end: true },
  { suffix: "360", label: "Project 360", icon: Compass, testId: "sidebar-link-project-360" },
  { suffix: "site-hub", label: "Site Management Hub", icon: LayoutGrid, testId: "sidebar-link-site-hub" },
  { suffix: "settings", label: "Project Settings", icon: Settings, testId: "sidebar-link-settings" },
  { suffix: "measurements", label: "Project Measurements", icon: Ruler, testId: "sidebar-link-measurements" },
  { suffix: "wbs", label: "WBS & Weightages", icon: GitFork, testId: "sidebar-link-wbs" },
  { suffix: "resources", label: "Resources & Manpower", icon: Users, testId: "sidebar-link-resources" },
  { suffix: "estimates", label: "Estimates & Budgets", icon: Calculator, testId: "sidebar-link-estimates" },
  { suffix: "schedule", label: "Master Schedule", icon: CalendarDays, testId: "sidebar-link-schedule" },
  { suffix: "resource-planning", label: "Resource Planning", icon: Gauge, testId: "sidebar-link-resource-planning" },
  { suffix: "engineering", label: "Engineering DPR", icon: DraftingCompass, testId: "sidebar-link-engineering" },
  { suffix: "materials", label: "Materials DPR", icon: Boxes, testId: "sidebar-link-materials" },
  { suffix: "site-inventory", label: "Site Inventory", icon: PackageOpen, testId: "sidebar-link-site-inventory" },
  { suffix: "activity-tracker", label: "Activity Tracker", icon: GitFork, testId: "sidebar-link-activity-tracker" },
  { suffix: "construction", label: "Construction & Measurements", icon: HardHat, testId: "sidebar-link-construction" },
  { suffix: "daily-reports", label: "Daily Progress Reports", icon: FileText, testId: "sidebar-link-daily-reports" },
  { suffix: "quality", label: "Quality & Handover", icon: ShieldCheck, testId: "sidebar-link-quality" },
  { suffix: "site-expenses", label: "Site Expenses", icon: Receipt, testId: "sidebar-link-site-expenses" },
  { suffix: "logbook", label: "Equipment Logbook", icon: Wrench, testId: "sidebar-link-logbook" },
  { suffix: "plan-vs-actual", label: "Plan vs Actual", icon: ClipboardList, testId: "sidebar-link-plan-vs-actual" },
  { suffix: "delays", label: "Delays", icon: Clock, testId: "sidebar-link-delays" },
  { suffix: "issues", label: "Issues & Risks", icon: AlertTriangle, testId: "sidebar-link-issues" },
  { suffix: "alerts", label: "Alerts & Overdue", icon: BellRing, testId: "sidebar-link-alerts" },
  { suffix: "audit", label: "Audit Trail", icon: History, testId: "sidebar-link-audit" },
  { suffix: "cost-entries", label: "Cost Entries", icon: Receipt, testId: "sidebar-link-cost-entries" },
  { suffix: "variations", label: "Variations", icon: FileDiff, testId: "sidebar-link-variations" },
  { suffix: "finance", label: "Project Finance", icon: Landmark, testId: "sidebar-link-project-finance" },
  { suffix: "closeout", label: "Project Close-out", icon: CheckSquare, testId: "sidebar-link-closeout" },
];

// Groups PROJECT_DPR_PAGES (above) into sub-modules inside a project's own sidebar
// panel — purely a visual grouping; every route/testId above is unchanged.
export const PROJECT_NAV_GROUPS = [
  { id: "overview", label: "Overview", icon: LayoutGrid, suffixes: ["", "360", "site-hub"] },
  { id: "plan", label: "Plan", icon: CalendarDays, suffixes: ["settings", "measurements", "wbs", "resources", "estimates", "schedule", "resource-planning"] },
  { id: "buy", label: "Buy", icon: ShoppingCart, suffixes: ["materials", "site-inventory", "activity-tracker"] },
  { id: "build", label: "Build", icon: HardHat, suffixes: ["engineering", "construction", "daily-reports", "quality", "site-expenses", "logbook", "plan-vs-actual", "delays", "issues", "alerts"] },
  { id: "bill-pay", label: "Bill & Pay", icon: Landmark, suffixes: ["cost-entries", "variations", "finance"] },
  { id: "close", label: "Close", icon: CheckSquare, suffixes: ["closeout", "audit"] },
];

function projectSubGroupFor(suffix) {
  const g = PROJECT_NAV_GROUPS.find((grp) => grp.suffixes.includes(suffix));
  return g ? g.id : null;
}

// Raw nav tree. Leaves without an explicit `to` route to the shared ComingSoon
// page at /erp/:group/:module (auto-generated below). What a person sees is decided by the Access page
// (MENU_MODULES below); `orgAdminOnly` is kept only for pages whose server side is Admin-only.
const RAW_GROUPS = [
  {
    id: "global", label: "Global", icon: LayoutDashboard,
    items: [
      { id: "workspace", label: "My Workspace", icon: UserCircle, to: "/workspace", testId: "sidebar-link-my-workspace" },
      { id: "clock", label: "Clock in / out", icon: CalendarDays, to: "/clock", testId: "sidebar-link-clock" },
      { id: "portfolio", label: "Portfolio Overview", icon: LayoutDashboard, to: "/", end: true, testId: "sidebar-link-portfolio" },
      { id: "performance", label: "Performance", icon: Gauge, to: "/performance", testId: "sidebar-link-performance" },
    ],
  },
  {
    id: "finance-and-accounting", label: "Finance & Accounting", icon: Landmark,
    items: [
      {
        id: "general-ledger", label: "General Ledger", icon: BookOpen,
        children: [
          { id: "ledger-tally-export", label: "Ledger & Tally export", icon: Download       },
          { id: "chart-of-accounts",  label: "Chart of Accounts",    icon: BookOpen       },
          { id: "post-journal",        label: "Post Journal",          icon: PenSquare      },
          { id: "periods-year-end",    label: "Periods & Year-End",    icon: CalendarDays   },
          { id: "inter-company-loans", label: "Inter-company loans",   icon: ArrowRightLeft, multiCompanyOnly: true },
        ],
      },
    ],
  },
  {
    id: "operations", label: "Operations", icon: Layers,
    items: [
      {
        id: "procurement", label: "Procurement", icon: ShoppingCart,
        children: [
          { id: "pipeline",              label: "Pipeline",              icon: Activity    },
          { id: "vendors",               label: "Vendors",               icon: Users2      },
          { id: "items-and-prices",      label: "Items & Prices",        icon: Tag         },
          { id: "purchase-indents",      label: "Purchase Indents",      icon: FileText    },
          { id: "purchase-requisitions", label: "Purchase Requisitions", icon: ClipboardList},
          { id: "rfqs",                  label: "RFQs",                  icon: FileSearch  },
          { id: "purchase-orders",       label: "Purchase Orders",       icon: ShoppingCart},
          { id: "goods-receipt-notes",   label: "Goods Receipt Notes",   icon: PackageCheck},
          { id: "service-requisitions",  label: "Service Requisitions",  icon: Wrench      },
          { id: "work-orders",           label: "Work Orders",           icon: Hammer      },
          { id: "service-bills",         label: "Service Bills",         icon: Receipt     },
          { id: "purchase-bills",        label: "Purchase Bills",        icon: CreditCard  },
          { id: "contractor-jmrs",       label: "Contractor JMRs",      icon: HardHat     },
          { id: "vendor-advances",       label: "Vendor Advances",       icon: PiggyBank   },
          { id: "vendor-payments",       label: "Vendor Payments",       icon: CreditCard  },
          { id: "vendor-ledger",         label: "Vendor Ledger",         icon: BookOpen    },
          { id: "debit-notes",           label: "Debit Notes",           icon: FileMinus   },
          { id: "procurement-tracker",   label: "Procurement Tracker",   icon: Activity    },
        ],
      },
      {
        id: "inventory", label: "Inventory", icon: PackageSearch,
        children: [
          { id: "warehouses",        label: "Warehouses",      icon: Warehouse        },
          { id: "stock-and-balances",label: "Stock & Balances",icon: BarChart3        },
          { id: "receipts",          label: "Receipts",        icon: PackageCheck     },
          { id: "goods-issues",      label: "Goods Issues",    icon: PackageOpen      },
          { id: "store-returns",     label: "Store Returns",   icon: Undo2            },
          { id: "adjustments",       label: "Adjustments",     icon: SlidersHorizontal},
          { id: "transfers",         label: "Transfers",       icon: ArrowRightLeft   },
        ],
      },
      {
        id: "masters", label: "Masters", icon: SlidersHorizontal,
        children: [
          { id: "companies",          label: "Companies",          icon: Building2, testId: "sidebar-link-companies", soloLabel: "Company Profile" },
          { id: "branches",           label: "Branches",           icon: GitBranch, testId: "sidebar-link-branches" },
          { id: "units-of-measure",   label: "Units of Measure",   icon: Ruler              },
          { id: "designations",       label: "Designations",       icon: UserCircle         },
          { id: "tax-codes",          label: "Tax Codes",          icon: Percent            },
          { id: "bank-accounts", label: "Bank Accounts", icon: Landmark },
          { id: "numbering-series",   label: "Numbering Series",   icon: Hash               },
          { id: "approval-workflows", label: "Approval Workflows", icon: GitBranch          },
        ],
      },
    ],
  },
  {
    id: "delivery", label: "Delivery", icon: Truck,
    items: [
      {
        id: "client-management", label: "Client Management", icon: Users2,
        children: [
          { id: "sales-flow",       label: "Sales Flow",        icon: TrendingUp     },
          { id: "clients",          label: "Clients",           icon: Users2         },
          { id: "proposals",        label: "Proposals",         icon: FileText       },
          { id: "ra-bills",         label: "RA Bills",          icon: Receipt        },
          { id: "variation-register", label: "Variation Register", icon: FileDiff     },
          { id: "client-payments",  label: "Client Payments",   icon: CreditCard     },
          { id: "client-ledger",    label: "Client Ledger",     icon: BookOpen       },
          { id: "client-advances",  label: "Client Advances",   icon: PiggyBank      },
        ],
      },
      {
        id: "projects", label: "Projects", icon: FolderKanban,
        children: [
          { id: "all-projects", label: "All projects" },
          { id: "setup-library", label: "Setup Library" },
          { id: "solar-progress-dpr", label: "Project progress (DPR)", isProjectSection: true },
        ],
      },
      { id: "production", label: "Production", icon: Factory, children: [
        { id: "plants-and-bom", label: "Plants & BOM" },
        { id: "production-runs", label: "Production Runs" },
        { id: "dispatches", label: "Dispatches" },
      ] },
    ],
  },
  {
    id: "assets-and-plant", label: "Assets & Plant", icon: Warehouse,
    items: [
      { id: "fixed-assets", label: "Fixed Assets", icon: Building, children: [
        { id: "asset-register",    label: "Asset Register",    icon: Building2    },
        { id: "depreciation-runs", label: "Depreciation Runs", icon: TrendingDown },
      ] },
      { id: "plant-and-machinery", label: "Plant & Machinery", icon: Wrench, children: [
        { id: "equipment",   label: "Equipment",   icon: Wrench },
        { id: "hire-orders", label: "Hire Orders", icon: Hammer },
        { id: "weighbridge", label: "Weighbridge", icon: Scale  },
      ] },
    ],
  },
  {
    id: "people-and-insight", label: "People & Insight", icon: UsersRound,
    items: [
      { id: "hr-and-payroll", label: "HR & Payroll", icon: Users, children: [
        { id: "employees",    label: "Employees",    icon: Users      },
        { id: "attendance",   label: "Attendance",   icon: CalendarDays},
        { id: "leave",        label: "Leave",        icon: CalendarDays},
        { id: "payroll-runs", label: "Payroll Runs", icon: DollarSign },
      ] },
      { id: "statutory", label: "Statutory", icon: ShieldCheck },
      { id: "reports-and-mis", label: "Reports & MIS", icon: BarChart3, children: [
        { id: "executive-dashboard", label: "Executive Dashboard", icon: Gauge },
        { id: "financial-statements", label: "Financial Statements", icon: FileSpreadsheet },
        { id: "finance-summary", label: "Finance Summary", icon: FileSpreadsheet },
        { id: "aging-reports", label: "Aging Reports", icon: History },
        { id: "cash-forecast", label: "Cash Forecast", icon: PiggyBank },
        { id: "cost-to-completion", label: "Cost to Completion", icon: TrendingUp },
        { id: "operations-reports", label: "Operations Reports", icon: Percent },
      ] },
    ],
  },
  {
    id: "governance", label: "Governance", icon: Scale,
    items: [
      { id: "legal-and-liaison", label: "Legal & Liaison", icon: Scale, children: [
        { id: "agreements", label: "Agreements", icon: ScrollText },
        { id: "statutory-approvals", label: "Statutory Approvals", icon: KeyRound },
        { id: "cases-and-notices", label: "Cases & Notices", icon: Gavel },
      ] },
    ],
  },
  {
    // Same pages as the sidebar's Administration menu, so it can also be a top-bar module.
    // Every page here is Admin-only on the server too, so the items are `orgAdminOnly`.
    id: "administration", label: "Administration", icon: Settings2,
    items: [
      { id: "admin-users", orgAdminOnly: true, label: "Users", icon: Users2, to: "/erp/governance/users", testId: "sidebar-link-admin-users" },
      { id: "admin-roles-access", orgAdminOnly: true, label: "Access", icon: KeyRound, to: "/erp/governance/access", testId: "sidebar-link-admin-roles-access" },
      { id: "admin-kpi-settings", orgAdminOnly: true, label: "Alert thresholds", icon: Gauge, to: "/erp/governance/kpi-settings", testId: "sidebar-link-admin-kpi-settings" },
      { id: "admin-access-history", orgAdminOnly: true, label: "Access history", icon: History, to: "/erp/governance/access-history", testId: "sidebar-link-admin-access-history" },
      { id: "admin-audit-log", orgAdminOnly: true, label: "Audit Log", icon: History, to: "/erp/governance/audit-log", testId: "sidebar-link-admin-audit-log" },
      { id: "admin-org-settings", orgAdminOnly: true, label: "Organization Settings", icon: Settings, to: "/erp/governance/org-settings", testId: "sidebar-link-admin-org-settings" },
      { id: "admin-portal-access", orgAdminOnly: true, label: "Portal access", icon: Users, to: "/erp/governance/portal-access", testId: "sidebar-link-admin-portal-access" },
      { id: "admin-system-health", orgAdminOnly: true, label: "Backup & Health", icon: HeartPulse, to: "/erp/governance/system-health", testId: "sidebar-link-admin-system-health" },
      { id: "admin-integrity-checks", orgAdminOnly: true, label: "Integrity Checks", icon: ShieldCheck, to: "/erp/governance/integrity-checks", testId: "sidebar-link-admin-integrity-checks" },
    ],
  },
];

function enrich(node, groupSlug, path, routeLabels, groupLabel) {
  const id = [...path, node.id].join(".");
  const slug = slugify(node.label);
  if (node.isProjectSection) {
    return { ...node, id, slug };
  }
  if (node.children) {
    const children = node.children.map((c) => enrich(c, groupSlug, [...path, node.id], routeLabels, groupLabel));
    return { ...node, id, slug, children };
  }
  let to = node.to;
  let testId = node.testId;
  if (!to) {
    to = `/erp/${groupSlug}/${slug}`;
    routeLabels[`${groupSlug}/${slug}`] = { label: node.label, groupLabel };
  }
  if (!testId) testId = `sidebar-link-${slug}`;
  return { ...node, id, slug, to, testId };
}

export const ROUTE_LABELS = {};

export const NAV_GROUPS = RAW_GROUPS.map((group) => {
  const groupSlug = slugify(group.label);
  const items = group.items.map((item) => enrich(item, groupSlug, [group.id], ROUTE_LABELS, group.label));
  return { ...group, slug: groupSlug, items };
});

function filterItemsByRole(items, ctx) {
  return items
    .filter((i) => (!i.restricted || ctx.seesWholePortfolio) && (!i.orgAdminOnly || ctx.isOrgAdmin) && (!i.multiCompanyOnly || navFlags.multiCompany))
    .map((i) => (i.soloLabel && !navFlags.multiCompany ? { ...i, label: i.soloLabel } : i))
    .map((i) => (i.children ? { ...i, children: filterItemsByRole(i.children, ctx) } : i))
    .filter((i) => i.isProjectSection || !i.children || i.children.length > 0);
}

export function applyRoleFilter(groups, ctx) {
  return groups
    .filter((g) => !g.restricted || ctx.seesWholePortfolio)
    .map((g) => ({ ...g, items: filterItemsByRole(g.items, ctx) }))
    .filter((g) => g.items.length > 0);
}

function matches(label, q) {
  return label.toLowerCase().includes(q);
}

function filterNode(node, q, projectId) {
  if (node.isProjectSection) {
    if (!projectId) return null;
    const dynamicChildren = PROJECT_DPR_PAGES.filter((p) => matches(p.label, q));
    if (dynamicChildren.length === 0 && !matches(node.label, q)) return null;
    return { ...node, dynamicChildren: dynamicChildren.length ? dynamicChildren : PROJECT_DPR_PAGES };
  }
  if (node.children) {
    const children = node.children.map((c) => filterNode(c, q, projectId)).filter(Boolean);
    if (children.length === 0 && !matches(node.label, q)) return null;
    return { ...node, children: children.length ? children : node.children };
  }
  return matches(node.label, q) ? node : null;
}

export function filterGroups(groups, q, projectId) {
  if (!q) return groups;
  return groups
    .map((g) => {
      const items = g.items.map((i) => filterNode(i, q, projectId)).filter(Boolean);
      if (items.length === 0 && !matches(g.label, q)) return null;
      return { ...g, items: items.length ? items : g.items };
    })
    .filter(Boolean);
}

export function flattenLeaves(groups, projectId) {
  const out = [];
  function walk(node) {
    if (node.isProjectSection) {
      (node.dynamicChildren || (projectId ? PROJECT_DPR_PAGES : [])).forEach((p) => {
        out.push({ to: `/projects/${projectId}/${p.suffix}`, label: p.label });
      });
      return;
    }
    if (node.children) {
      node.children.forEach(walk);
      return;
    }
    out.push({ to: node.to, label: node.label });
  }
  groups.forEach((g) => g.items.forEach(walk));
  return out;
}

export function findActivePath(groups, pathname, projectId) {
  function isLeafActive(to) {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(`${to}/`);
  }
  // Picks the most specific PROJECT_DPR_PAGES match for `pathname` (not just the first
  // one whose prefix happens to match) — needed since Dashboard's route ("") is itself
  // a prefix of every other project page's route.
  function matchProjectPage(pathname, projectId) {
    let best = null;
    for (const p of PROJECT_DPR_PAGES) {
      const to = `/projects/${projectId}/${p.suffix}`.replace(/\/$/, "");
      const hit = p.end ? pathname === to : isLeafActive(to);
      if (hit && (!best || to.length > best.to.length)) best = { page: p, to };
    }
    return best ? best.page : null;
  }
  function search(node, ancestors) {
    if (node.isProjectSection) {
      if (!projectId) return null;
      const match = matchProjectPage(pathname, projectId);
      if (!match) return null;
      const subGroupId = projectSubGroupFor(match.suffix);
      return subGroupId ? [...ancestors, node.id, `${node.id}.${subGroupId}`] : [...ancestors, node.id];
    }
    if (node.children) {
      for (const child of node.children) {
        const found = search(child, [...ancestors, node.id]);
        if (found) return found;
      }
      return null;
    }
    return isLeafActive(node.to) ? ancestors : null;
  }
  for (const group of groups) {
    for (const item of group.items) {
      const found = search(item, []);
      if (found) return found;
    }
  }
  return [];
}

export function isGroupActive(group, pathname, projectId) {
  function isLeafActive(to) {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(`${to}/`);
  }
  function check(node) {
    if (node.isProjectSection) {
      if (!projectId) return false;
      return PROJECT_DPR_PAGES.some((p) => isLeafActive(`/projects/${projectId}/${p.suffix}`.replace(/\/$/, "")));
    }
    if (node.children) return node.children.some(check);
    return isLeafActive(node.to);
  }
  return group.items.some(check);
}

// ---------------------------------------------------------------------------
// Permission-matrix module for each menu entry (matched on the entry's own id).
// Entries without a module are always visible. A list means "any of these".
// ---------------------------------------------------------------------------
const PAYROLL = "HR_PAYROLL";
export const MENU_MODULES = {
  import: "PROJECT_DPR",
  // Finance & Accounting
  "chart-of-accounts": "CHART_OF_ACCOUNTS", "post-journal": "JOURNAL_ENTRIES", "periods-year-end": "FISCAL_PERIODS",
  "ledger-tally-export": "TALLY_EXPORT", "inter-company-loans": "INTERCOMPANY_LOANS",
  // Masters
  companies: "ADMINISTRATION", branches: "ADMINISTRATION", "units-of-measure": "UNITS", "tax-codes": "TAX_CODES", "bank-accounts": "BANK_ACCOUNTS",
  "numbering-series": "NUMBERING_SERIES", "approval-workflows": "APPROVAL_WORKFLOWS", designations: "DESIGNATIONS",
  // Procurement
  "items-and-prices": ["PURCHASING", "STORES"], vendors: "VENDORS", "purchase-indents": "INDENTS", "purchase-requisitions": "REQUISITIONS", rfqs: "RFQS",
  "purchase-orders": "PURCHASE_ORDERS", pipeline: ["PURCHASING", "SUBCONTRACT"], "procurement-tracker": ["PURCHASING", "SUBCONTRACT"],
  "goods-receipt-notes": "GRN",
  "service-requisitions": "SERVICE_REQUISITIONS", "work-orders": "WORK_ORDERS", "contractor-jmrs": "JMR",
  "service-bills": "BILLS", "purchase-bills": "BILLS", "vendor-advances": "ADVANCES", "vendor-payments": "PAYMENTS",
  "vendor-ledger": "PAYABLES", "debit-notes": "BILLS",
  // Inventory
  items: ["STORES", "PURCHASING"], warehouses: "WAREHOUSES", "stock-and-balances": "STORES", receipts: "STORES", "goods-issues": "STOCK_MOVEMENTS",
  "store-returns": "STOCK_MOVEMENTS", adjustments: "STOCK_MOVEMENTS", transfers: "STOCK_MOVEMENTS",
  // Client management
  clients: "CLIENTS", "sales-flow": "RECEIVABLES", leads: "LEADS_TENDERS", tenders: "LEADS_TENDERS", proposals: "PROPOSALS", contracts: "CONTRACTS",
  "joint-measurement": "RECEIVABLES", "ra-bills": "RA_BILLS", "client-payments": "CLIENT_RECEIPTS", "client-ledger": "RECEIVABLES",
  "certify-ra-bills": "RA_BILLS", "client-advances": "CLIENT_RECEIPTS", "site-measurements": "RECEIVABLES", "variation-register": "VARIATIONS",
  // Projects
  "all-projects": "PROJECT_DPR", "setup-library": "WBS_TEMPLATES", "workbook-import": "PROJECT_DPR",
  // Production
  "plants-and-bom": ["PRODUCTION_PLANTS", "BOMS"], "production-runs": "PRODUCTION_RUNS", dispatches: "PRODUCTION_DISPATCHES",
  // Assets & Plant
  "asset-register": "ASSETS", "depreciation-runs": "ASSETS", equipment: "PLANT", "hire-orders": "HIRE_ORDERS",
  weighbridge: "PLANT",
  // People & Insight
  "hr-and-payroll": PAYROLL, employees: "EMPLOYEES", attendance: "ATTENDANCE", leave: "LEAVE", "payroll-runs": ["PAYROLL_RUNS", "PAYSLIPS", "PAYROLL_PAYMENTS"], statutory: "TDS",
  "executive-dashboard": "REPORTS_PROJECT", "financial-statements": "REPORTS_FINANCIAL", "finance-summary": "REPORTS_FINANCIAL", "aging-reports": "REPORTS_FINANCIAL", "cash-forecast": "REPORTS_FINANCIAL",
  "cost-to-completion": "REPORTS_PROJECT", "operations-reports": ["REPORTS_PROCUREMENT", "REPORTS_PROJECT"],
  // Governance
  agreements: "LEGAL_AGREEMENTS", "statutory-approvals": "STATUTORY_APPROVALS", "cases-and-notices": "LEGAL_CASES",
  users: "ADMINISTRATION", "permission-matrix": "ADMINISTRATION", "roles-access": "ADMINISTRATION", access: "ADMINISTRATION", "admin-roles-access": "ADMINISTRATION", "admin-access-history": "ADMINISTRATION", "access-history": "ADMINISTRATION", "audit-log": "ADMINISTRATION", "org-settings": "ADMINISTRATION", "portal-access": "ADMINISTRATION",
  "system-health": "ADMINISTRATION", "integrity-checks": "ADMINISTRATION",
};

// Mirrors backend app/core/project_access.py SEGMENT_MODULE, so the sidebar and the server agree on which row guards each page.
const PROJECT_PAGE_MODULES = {
  "": "PROJECT_OVERVIEW", "360": "PROJECT_OVERVIEW", "site-hub": "PROJECT_OVERVIEW",
  settings: "PROJECT_SETTINGS", measurements: "PROJECT_SETTINGS",
  wbs: "PROJECT_PLANNING", resources: "PROJECT_PLANNING", "resource-planning": "PROJECT_PLANNING", schedule: "PROJECT_PLANNING",
  engineering: "PROJECT_PROGRESS", materials: "PROJECT_PROGRESS", construction: "PROJECT_PROGRESS", "daily-reports": "PROJECT_PROGRESS", "plan-vs-actual": "PROJECT_PROGRESS",
  "site-inventory": "PROJECT_SITE_OPS", issues: "PROJECT_SITE_OPS", delays: "PROJECT_SITE_OPS", alerts: "PROJECT_SITE_OPS",
  quality: "SNAGS", "site-expenses": "PROJECT_SITE_EXPENSES", documents: "PROJECT_DOCUMENTS", closeout: "PROJECT_CLOSEOUT", audit: "PROJECT_CLOSEOUT",
  estimates: "PROJECT_BUDGET", "cost-entries": "PROJECT_COSTS", finance: "PROJECT_COSTS", variations: "VARIATIONS",
  "activity-tracker": "SUBCONTRACT", logbook: "PROJECT_LOGBOOK",
};

/** Module of a page inside a project (e.g. "finance" -> PROJECT_FINANCE, everything else PROJECT_DPR). */
export function projectPageModule(suffix) {
  return PROJECT_PAGE_MODULES[suffix] || "PROJECT_DPR";
}

function moduleOfNode(node) {
  if (node.module) return node.module;
  return MENU_MODULES[String(node.id).split(".").pop()];
}

/** Hide every menu entry whose module the user cannot VIEW; drop empty parents and groups. */
export function applyPermissionFilter(groups, canView) {
  const allowedPages = PROJECT_DPR_PAGES.filter((p) => canView(projectPageModule(p.suffix)));
  const walk = (items) => items
    .filter((i) => (i.isProjectSection ? allowedPages.length > 0 : i.children ? true : canView(moduleOfNode(i))))
    .map((i) => (i.isProjectSection ? { ...i, allowedPages } : i.children ? { ...i, children: walk(i.children) } : i))
    .filter((i) => i.isProjectSection || !i.children || i.children.length > 0);
  return groups.map((g) => ({ ...g, items: walk(g.items) })).filter((g) => g.items.length > 0);
}

/** Module that guards the page at `pathname` (null = not guarded). */
export function moduleForPath(pathname) {
  let best = null;
  const visit = (items) => items.forEach((node) => {
    if (node.children) return visit(node.children);
    if (!node.to || node.to === "/") return;
    if (pathname === node.to || pathname.startsWith(`${node.to}/`)) {
      if (!best || node.to.length > best.to.length) best = node;
    }
  });
  NAV_GROUPS.forEach((g) => visit(g.items));
  return best ? moduleOfNode(best) : null;
}
// ---------------------------------------------------------------------------
// Top navigation bar: groups the existing modules above into a horizontal
// bar (Dashboard · Projects · Finance · Procurement · Construction ·
// Client Management · Assets & Plant · HR & Payroll · Governance · Reports).
// This does NOT duplicate any module — each entry just picks existing nodes
// out of NAV_GROUPS (already enriched with `to` routes, icons, restrictions)
// so the sidebar can show only the slice for whichever top item is active.
// ---------------------------------------------------------------------------

// Leaf ids inside operations→procurement split between buying materials
// (Procurement), subcontract execution (Construction), and the bills /
// payments that now live under their own top-nav items instead.
const CONSTRUCTION_EXECUTION_IDS = ["service-requisitions", "work-orders", "contractor-jmrs"];
const MATERIAL_PROCUREMENT_IDS = ["pipeline", "vendors", "items-and-prices", "purchase-indents", "purchase-requisitions", "rfqs", "purchase-orders", "goods-receipt-notes", "procurement-tracker"];
const CLIENT_MANAGEMENT_IDS = ["sales-flow", "clients", "proposals", "site-measurements", "joint-measurement", "certify-ra-bills"];
const VENDOR_BILL_IDS = ["purchase-bills", "service-bills", "debit-notes"];
const VENDOR_PAYMENT_IDS = ["vendor-advances", "vendor-payments", "vendor-ledger"];
const CLIENT_BILL_IDS = ["ra-bills", "variation-register"];
const CLIENT_PAYMENT_IDS = ["client-advances", "client-payments", "client-ledger"];

export const TOP_NAV = [
  // Order follows the work: set up -> client & contract -> project -> buy -> store -> produce -> plant -> people ->
  // accounts -> statutory -> reports -> admin. Bills and payments sit with the module that owns the document.
  { id: "workspace", label: "My Workspace", icon: LayoutDashboard, panels: [{ groupId: "global" }] },
  {
    id: "masters", label: "Masters", icon: SlidersHorizontal,
    panels: [{ groupId: "operations", childId: "masters" }],
  },
  {
    id: "client-management", label: "Client Management", icon: Users2,
    panels: [
      { groupId: "delivery", childId: "client-management", pickIds: CLIENT_MANAGEMENT_IDS },
      { groupId: "delivery", childId: "client-management", pickIds: CLIENT_BILL_IDS, label: "Client Bills" },
      { groupId: "delivery", childId: "client-management", pickIds: CLIENT_PAYMENT_IDS, label: "Client Payments" },
    ],
  },
  { id: "projects", label: "Projects & Portfolio", icon: FolderKanban, panels: [{ groupId: "delivery", childId: "projects" }] },
  {
    id: "procurement", label: "Procurement", icon: ShoppingCart,
    panels: [
      { groupId: "operations", childId: "procurement", pickIds: MATERIAL_PROCUREMENT_IDS },
      { groupId: "operations", childId: "procurement", pickIds: CONSTRUCTION_EXECUTION_IDS, label: "Subcontract Execution" },
      { groupId: "operations", childId: "procurement", pickIds: VENDOR_BILL_IDS, label: "Vendor & Subcontractor Bills" },
      { groupId: "operations", childId: "procurement", pickIds: VENDOR_PAYMENT_IDS, label: "Vendor Payments" },
    ],
  },
  {
    id: "inventory", label: "Inventory", icon: PackageSearch,
    panels: [{ groupId: "operations", childId: "inventory", pickIds: ["stock-and-balances", "warehouses", "receipts", "goods-issues", "store-returns", "transfers", "adjustments"] }],
  },
  { id: "production", label: "Production", icon: Factory, panels: [{ groupId: "delivery", childId: "production" }] },
  { id: "assets-and-plant", label: "Assets & Plant", icon: Warehouse, panels: [{ groupId: "assets-and-plant" }] },
  { id: "hr-and-payroll", label: "HR & Payroll", icon: Users, panels: [{ groupId: "people-and-insight", childId: "hr-and-payroll" }] },
  { id: "finance", label: "Finance & Accounting", icon: Landmark, panels: [{ groupId: "finance-and-accounting", childId: "general-ledger" }] },
  {
    id: "governance", label: "Governance & Statutory", icon: Scale,
    panels: [
      { groupId: "people-and-insight", childId: "statutory", label: "Statutory" },
      { groupId: "governance", childId: "legal-and-liaison", label: "Legal & Liaison" },
    ],
  },
  { id: "reports", label: "Reports & MIS", icon: BarChart3, panels: [{ groupId: "people-and-insight", childId: "reports-and-mis" }] },
  { id: "administration", label: "Administration", icon: Settings2, panels: [{ groupId: "administration" }] },
];

/** Visual groupings for TOP_NAV — items with a matching `group` key are collapsed
 *  into a single dropdown button in the topbar. */
export const TOP_NAV_GROUPS = [
  { id: "operations", label: "Operations", icon: Layers },
];

// enrich() gives every item a compound dotted id (e.g. "operations.procurement"),
// built from its RAW (un-prefixed) id at each level — so the last "."-segment is
// always that item's own original id, whatever depth it sits at.
function lastSeg(id) {
  return typeof id === "string" ? id.split(".").pop() : id;
}

function panelGroup(navGroups, spec, idx, topId) {
  const group = navGroups.find((g) => g.id === spec.groupId);
  if (!group) return null;
  let node = group;
  let label = spec.label || group.label;
  if (spec.childId) {
    node = group.items.find((i) => lastSeg(i.id) === spec.childId);
    if (!node) return null;
    label = spec.label || node.label;
  }
  let items;
  if (spec.pickIds) {
    const kids = node.children || [];
    items = spec.pickIds.map((id) => kids.find((c) => lastSeg(c.id) === id)).filter(Boolean);
  } else if (node.children) {
    items = node.children;
  } else if (node.items) {
    items = node.items; // whole group requested (no childId) — e.g. "global", "assets-and-plant"
  } else {
    items = [node];
  }
  if (!items || items.length === 0) return null;
  return { id: `${topId}.panel${idx}`, label, icon: node.icon || group.icon, items };
}

/** The sidebar-ready groups (already role/permission filtered, since `navGroups`
 * is whatever the caller already filtered) for one top-nav item. */
export function topNavPanels(navGroups, topId) {
  const entry = TOP_NAV.find((t) => t.id === topId);
  if (!entry) return [];
  return entry.panels.map((spec, idx) => panelGroup(navGroups, spec, idx, topId)).filter(Boolean);
}

/** Which top-nav item the current URL belongs to (null if none — e.g. a
 * project page, which every top item leaves alone so ProjectShell's own nav applies). */
export function activeTopNavId(navGroups, pathname, projectId) {
  for (const entry of TOP_NAV) {
    const panels = topNavPanels(navGroups, entry.id);
    if (panels.some((g) => isGroupActive(g, pathname, projectId))) return entry.id;
  }
  return pathname === "/" ? "dashboard" : null;
}

/** First navigable link inside a top-nav item — used so clicking a top button
 * (e.g. "Finance") always lands on a real page, its first module. */
export function firstLeafOf(navGroups, topId, projectId) {
  const panels = topNavPanels(navGroups, topId);
  const leaves = flattenLeaves(panels, projectId);
  return leaves[0]?.to || null;
}

/** Which top-nav items have anything to show for this user (drives which
 * buttons even render in the bar). */
export function visibleTopNavIds(navGroups) {
  return TOP_NAV.filter((entry) => topNavPanels(navGroups, entry.id).length > 0).map((e) => e.id);
}
